import React, { useEffect, useState } from 'react'
import { authService, type OrganizationInvitePreview } from '../../services/auth'
import { useI18n } from '../../i18n/I18nContext'
import { useAuth } from '../../context/AuthContext'
import { LoginForm } from './LoginForm'

interface InviteAcceptFormProps {
  token: string
  onAccepted?: () => void
}

export const InviteAcceptForm: React.FC<InviteAcceptFormProps> = ({ token, onAccepted }) => {
  const { t } = useI18n()
  const { user, logout } = useAuth()
  const [accepted, setAccepted] = useState(false)
  const [invite, setInvite] = useState<OrganizationInvitePreview | null>(null)
  const existing = invite?.acceptanceMode === 'existing_account'
  const matchingOwner = Boolean(user && invite && user.email.toLowerCase() === invite.email.toLowerCase())
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    authService.getOrganizationInvite(token)
      .then((row) => mounted && setInvite(row))
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Invite not found or expired.')))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [t, token])

  const acceptInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    if (password && password.length < 8) {
      setError(t('Use at least 8 characters for the password.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await authService.acceptOrganizationInvite(token, existing ? {} : {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        password: password || undefined,
      })
      setAccepted(true)
      setPassword('')
      setMessage(t('Invitation accepted. Sign in again to load your updated access.'))
      onAccepted?.()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to accept invite.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="helper">{t('Loading invite...')}</p>

  if (!accepted && invite?.acceptanceMode === 'new_account' && user) return (
    <div className="stack">
      <p className="helper">{t('Sign out before setting up the invited account.')}</p>
      <button type="button" className="btn btn-secondary" onClick={() => void logout()}>{t('Sign out')}</button>
    </div>
  )

  if (!accepted && existing && !matchingOwner) return (
    <div className="stack">
      <p className="helper">{t('Sign in with the invited account before accepting this invitation.')}</p>
      <p className="text-muted">{invite?.email}</p>
      {user ? <button type="button" className="btn btn-secondary" onClick={() => void logout()}>{t('Sign out')}</button> : <LoginForm />}
    </div>
  )

  return (
    <form className="stack" onSubmit={acceptInvite} data-testid="invite-accept-form">
      {invite ? (
        <div className="signup-success">
          <span className="status-pill status-pill--confirmed">{t('Organization invite')}</span>
          <h2 className="heading-lg">{invite.organizationName || t('Organization')}</h2>
          <p className="text-muted">{invite.email}</p>
        </div>
      ) : null}
      {!existing && !accepted && invite?.acceptanceMode !== 'unavailable' ? <>
      <div className="admin-two-column">
        <div className="form-row">
          <label htmlFor="inviteFirstName">{t('First name optional')}</label>
          <input id="inviteFirstName" className="input" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </div>
        <div className="form-row">
          <label htmlFor="inviteLastName">{t('Last name optional')}</label>
          <input id="inviteLastName" className="input" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <label htmlFor="invitePhone">{t('Phone optional')}</label>
        <input id="invitePhone" className="input" value={phone} onChange={(event) => setPhone(event.target.value)} />
      </div>
      <div className="form-row">
        <label htmlFor="invitePassword">{t('Password')}</label>
        <input id="invitePassword" className="input" type="password" required value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t('Minimum 8 characters')} />
      </div>
      </> : null}
      {invite?.acceptanceMode === 'unavailable' ? <p className="error-text">{t('This account requires administrator review.')}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
      {message ? <p className="success-text">{message}</p> : null}
      {accepted ? <a href="/login">{t('Sign in')}</a> : null}
      <button type="submit" className="btn btn-primary" disabled={saving || !invite || accepted || invite.acceptanceMode === 'unavailable'}>{saving ? t('Accepting...') : t('Accept invite')}</button>
    </form>
  )
}
